<template>
  <div>
    <PageAllow controller="SubAccountConfigs" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1 pe-2">
            <SearcherKeyword
              ref="keyword"
              v-model="options.keyword"
              @text="keyword = $event"
              :label="$t('SubAccountConfig.Keyword')"
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              small
              color="primary"
              class="ms-1"
              @click.stop="
                $permission.checkAccountExpired().then(() => {
                  $router.replace({
                    path: '/settings/sub-account-settings/sub-account-configs/0',
                    query: subAccountConfigs.length
                      ? { type: options.type, mode: 'new' }
                      : { type: options.type, mode: 'defaultNew' },
                  });
                })
              "
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{
                subAccountConfigs.length
                  ? $t("CRUD.newInfo", [$t("SubAccountConfig.title")])
                  : $t("SubAccountConfig.addDefaultConfig")
              }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-radio-group row v-model="options.type" dense class="mb-n4">
              <v-radio
                :label="$t('SubAccountConfig.types.dishConfigs')"
                :key="1"
                :value="1"
                class="me-1"
              />
              <v-radio
                :label="$t('SubAccountConfig.types.materialConfigs')"
                :key="2"
                :value="2"
                class="me-1"
              />
            </v-radio-group>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="subAccountConfigs"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-2 px-6"
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.type="{ item, index }">
            <span :v-resize="!index ? $stickTableResizer() : null"> </span>
            <v-chip
              small
              label
              :color="item.type | subAccountConfigTypesColor"
              :text-color="item.type | subAccountConfigTypesTextColor"
            >
              {{ item.type | subAccountConfigTypesText }}

              {{
                item.default_flg
                  ? "(" + $t("SubAccountConfig.Fields.default_flg") + ")"
                  : ""
              }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item, index }">
            <span :v-resize="!index ? $stickTableResizer() : null"> </span>
            <v-chip
              small
              label
              :color="item.status | subAccountConfigStatusColor"
              :text-color="item.status | subAccountConfigStatusTextColor"
            >
              {{ item.status | subAccountConfigStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <!-- 详情 -->
            <v-tooltip
              top
              v-if="$permission.permissionAllow('SubAccountConfigs', 'view')"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="secondary"
                  class="px-1 me-1"
                  @click.stop="
                    $permission.checkAccountExpired().then(() => {
                      $router.replace({
                        path:
                          '/settings/sub-account-settings/sub-account-configs/' +
                          item.id,
                        query: { type: item.type, mode: 'view' },
                      });
                    })
                  "
                  >fa-file-alt
                </v-icon>
              </template>
              <span>
                {{ $t("CRUD.viewInfo", [$t("SubAccountConfig.ObjectName")]) }}
              </span>
            </v-tooltip>
            <v-tooltip
              top
              v-if="$permission.permissionAllow('SubAccountConfigs', 'edit')"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  class="px-1 py-4 me-1"
                  @click.stop="
                    $permission.checkAccountExpired().then(() => {
                      $router.push({
                        path:
                          '/settings/sub-account-settings/sub-account-configs/' +
                          item.id,
                        query: { type: item.type, mode: 'edit' },
                      });
                    })
                  "
                  >fa-pencil-alt
                </v-icon>
              </template>
              <span>
                {{ $t("CRUD.editInfo", [$t("SubAccountConfig.ObjectName")]) }}
              </span>
            </v-tooltip>
            <v-tooltip
              top
              v-if="
                !item.default_flg &&
                !item.status &&
                $permission.permissionAllow('SubAccountConfigs', 'delete')
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="danger"
                  class="px-1 me-1"
                  @click.stop="
                    deleteItem = item;
                    deleteDialog = true;
                  "
                  >fa-trash-alt
                </v-icon>
              </template>
              <span>
                {{ $t("CRUD.deleteInfo", [$t("SubAccountConfig.ObjectName")]) }}
              </span>
            </v-tooltip>
          </template>
        </v-data-table>
      </div>
    </v-sheet>

    <DialogDelete
      v-model="deleteDialog"
      :label="$t('SubAccountConfig.ObjectName')"
      @confirm="
        del();
        deleteDialog = false;
      "
    />
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import DialogDelete from "@/components/plugins/DialogDelete";
import SubAccountConfigs from "@/api/sub_account_configs";

const defaultOptions = {
  keyword: "",
  type: 1,
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    DialogDelete,
    SearcherKeyword,
  },

  computed: {
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("SettingNav.split"),
          disabled: false,
          href: "/settings/sub-account-settings/dish-sub-account-configs",
        },
        {
          text: i18n.t("SettingNav.subAccountConfig"),
          disabled: true,
          href: "/settings/sub-account-settings/dish-sub-account-configs",
        },
      ];
    },

    headers() {
      return [
        {
          text: i18n.t("SubAccountConfig.Fields.name"),
          align: "start",
          sortable: false,
          value: "name",
        },
        {
          text: i18n.t("SubAccountConfig.Fields.type"),
          align: "start",
          sortable: false,
          value: "type",
        },
        {
          text: i18n.t("SubAccountConfig.Fields.status"),
          align: "start",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("SubAccountConfig.Fields.memo"),
          align: "start",
          sortable: false,
          value: "memo",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    keyword: "",
    options: JSON.parse(
      sessionStorage.getItem("subAccountConfigs-options")
        ? sessionStorage.getItem("subAccountConfigs-options")
        : JSON.stringify(defaultOptions)
    ),
    subAccountConfigs: [],
    paging: {},
    deleteDialog: false,
    deleteItem: {},
  }),

  mounted() {
    this.$commonUtils.loadOptions("subAccountConfigs").then(
      function (data) {
        if (data.options) {
          this.options = data.options;
        } else this.resetSearch();
      }.bind(this)
    );
    if (this.$route.query.type) {
      this.options.type = this.$route.query.type;
      this.$router.replace(
        "/settings/sub-account-settings/sub-account-configs"
      );
    }
  },

  methods: {
    resetSearch() {
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.keyword, this.$refs.login_user_id].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.keyword,
      });
    },
    getSubAccountConfigs() {
      SubAccountConfigs.fetch(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.subAccountConfigs = response.data.sub_account_configs;
          }
        }.bind(this)
      );
    },
    del() {
      SubAccountConfigs.del(this.deleteItem.id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getSubAccountConfigs(); // 删除成功的话刷新数据
          }
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getSubAccountConfigs();
        sessionStorage.setItem(
          "subAccountConfigs-options",
          JSON.stringify(this.options)
        );
      },
    },
  },
};
</script>